// src/pages/GroupCitizen.js
// Citizen-specific workspace for search/selection, previous Visit review,
// active-Visit continuation, and Citizen-linked records.
//
// Design rules:
// - Comprehensive Services remains the main operational launch hub.
// - Clinical record cards always open an explicitly selected Visit.
// - A Citizen may stay selected without carrying another Citizen's Visit.
// - Canonical Citizen and Visit APIs are preferred; narrow compatibility
//   fallbacks are retained only for older deployments.
// - Tenant-aware navigation, drag-and-drop ordering, role visibility, and
//   existing workspace actions are preserved.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  FileText,
  FileSearch,
  History,
  NotebookPen,
  FolderOpen,
  GripVertical,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import CitizenSearchBar from '../components/CitizenSearchBar';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCitizen } from '../CitizenContext';
import http from '../services/http';

const baseSections = [
  {
    label: 'Citizen Prescriptions / नागरिक के पर्चे',
    icon: <FileText size={32} />,
    path: 'prescriptions',
    requiresVisit: true,
    roles: [
      'faculty',
      'intern',
      'junior resident',
      'senior resident',
      'medical officer',
      'admin',
      'citizen',
    ],
  },
  {
    label: 'Citizen Investigations / नागरिक की जांचें',
    icon: <FileSearch size={32} />,
    path: 'investigations',
    requiresVisit: true,
    roles: [
      'faculty',
      'intern',
      'junior resident',
      'senior resident',
      'medical officer',
      'admin',
      'citizen',
    ],
  },
  {
    label: 'Citizen Medical History / नागरिक का स्वास्थ्य इतिहास',
    icon: <History size={32} />,
    path: 'medical-history',
    requiresVisit: true,
    roles: [
      'faculty',
      'intern',
      'junior resident',
      'senior resident',
      'medical officer',
      'admin',
      'citizen',
    ],
  },
  {
    label: 'Add Clinical Record / नैदानिक रिकॉर्ड जोड़ें',
    icon: <NotebookPen size={32} />,
    path: 'exam',
    requiresVisit: true,
    roles: [
      'faculty',
      'intern',
      'junior resident',
      'senior resident',
      'medical officer',
      'admin',
    ],
  },
  {
    label: 'Upload Documents / दस्तावेज़ अपलोड करें',
    icon: <FolderOpen size={32} />,
    path: 'upload-documents',
    requiresVisit: false,
    roles: [
      'faculty',
      'intern',
      'junior resident',
      'senior resident',
      'medical officer',
      'admin',
      'deo',
    ],
  },
  {
    label: 'Upload Prescription Slip / पर्चा अपलोड करें',
    icon: <FolderOpen size={32} />,
    path: 'upload-slip',
    requiresVisit: false,
    roles: ['deo'],
  },
];

const CMFM_SEARCH_ROLES = new Set([
  'faculty',
  'intern',
  'junior resident',
  'senior resident',
  'medical officer',
  'admin',
  'deo',
]);

function stringValue(value) {
  if (value === undefined || value === null) return '';
  const normalized = String(value).trim();
  return normalized === 'undefined' || normalized === 'null' ? '' : normalized;
}

function normalizeRole(value) {
  const role = stringValue(value).toLowerCase();
  if (!role) return '';

  if (['jr', 'junior', 'juniorresident', 'junior resident'].includes(role)) {
    return 'junior resident';
  }
  if (['sr', 'senior', 'seniorresident', 'senior resident'].includes(role)) {
    return 'senior resident';
  }
  if (['mo', 'medical-officer', 'medical officer'].includes(role)) {
    return 'medical officer';
  }
  if (['administrator'].includes(role) || role.includes('admin')) return 'admin';
  if (['data entry operator', 'data-entry-operator'].includes(role)) return 'deo';

  return role;
}

function tenantBase() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return match ? match[0] : '';
  } catch {
    return '';
  }
}

function tenantize(path) {
  const base = tenantBase();
  const value = String(path || '');

  if (!base) return value;
  if (!value.startsWith('/')) {
    return `${base}/${value}`.replace(/\/{2,}/g, '/');
  }
  if (value.startsWith(base)) return value;

  return `${base}${value}`.replace(/\/{2,}/g, '/');
}

function readJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed === undefined || parsed === null ? fallback : parsed;
  } catch {
    return fallback;
  }
}

function normalizeCentreLabel(value) {
  const raw = stringValue(value);
  const normalized = raw
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return '';
  if (normalized.includes('keru')) return 'CRHA Keru';
  if (normalized.includes('dhawa')) return 'CRHA Dhawa';
  if (normalized.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    ['dundhara', 'dhundhara', 'dhundara', 'dundara'].some((part) =>
      normalized.includes(part)
    )
  ) {
    return 'CRHA Dhundhara';
  }

  return raw;
}

function normalizeCitizen(value, fallbackCitizenId = '') {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const citizenId = stringValue(
    value.citizenId ||
      value.cid ||
      value.id ||
      value._id ||
      fallbackCitizenId
  );
  if (!citizenId) return null;

  const { password: _password, qrCode: _qrCode, ...safeValue } = value;

  return {
    ...safeValue,
    citizenId,
    centre: normalizeCentreLabel(
      safeValue.centre ||
        safeValue.healthCentre ||
        safeValue.chc ||
        ''
    ),
  };
}

function unwrapCitizenResponse(response) {
  const payload =
    response && Object.prototype.hasOwnProperty.call(response, 'data')
      ? response.data
      : response;

  return (
    payload?.citizen ||
    payload?.data?.citizen ||
    payload?.result?.citizen ||
    payload?.data ||
    payload ||
    null
  );
}

function persistCitizenContext(value) {
  const citizen = normalizeCitizen(value);
  if (!citizen?.citizenId) return;

  try {
    localStorage.setItem('activeCitizen', JSON.stringify(citizen));
    localStorage.setItem('activeCitizenId', citizen.citizenId);
    localStorage.setItem('lastVisitedCitizenId', citizen.citizenId);

    if (citizen.centre) {
      localStorage.setItem('activeCentre', citizen.centre);
      localStorage.setItem('centre', citizen.centre);
    }
  } catch {
    // Browser-storage failure must not break the in-memory workspace.
  }
}

function getVisitId(visit) {
  return stringValue(visit?.visitId || visit?._id || visit?.id);
}

function getVisitStatusValue(visit) {
  return stringValue(visit?.prescription?.status || visit?.status).toLowerCase();
}

function getVisitStatusLabel(visit) {
  const status = getVisitStatusValue(visit);

  if (!status) return 'No saved visit yet';
  if (status === 'draft') return 'Draft visit available';
  if (status === 'pending' || status === 'submitted') {
    return 'Pending approval / incomplete flow';
  }
  if (status === 'returned') return 'Returned for editing';
  if (status === 'approved') return 'Latest visit approved';

  return status;
}

function getVisitTimestamp(visit) {
  const date = new Date(
    visit?.visitDate ||
      visit?.date ||
      visit?.updatedAt ||
      visit?.createdAt ||
      0
  );
  const timestamp = date.getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatVisitDate(visit) {
  const timestamp = getVisitTimestamp(visit);
  if (!timestamp) return 'Date not available';

  try {
    return new Date(timestamp).toLocaleString();
  } catch {
    return 'Date not available';
  }
}

function normalizeVisitList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.visits)) return payload.visits;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;
  if (Array.isArray(payload?.visits?.items)) return payload.visits.items;
  if (Array.isArray(payload?.data?.visits)) return payload.data.visits;
  return [];
}

function statusOf(errorOrResponse) {
  return Number(
    errorOrResponse?.status ||
      errorOrResponse?.response?.status ||
      errorOrResponse?.data?.status ||
      0
  );
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

function calculateAgeFromDob(rawDob) {
  if (!rawDob) return '';

  const dob = new Date(rawDob);
  const today = new Date();
  if (
    Number.isNaN(dob.getTime()) ||
    dob.getTime() > today.getTime()
  ) {
    return '';
  }

  const start = Date.UTC(
    dob.getUTCFullYear(),
    dob.getUTCMonth(),
    dob.getUTCDate()
  );
  const end = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );
  const days = Math.floor((end - start) / 86400000);

  if (days <= 59) {
    return `${days} day${days === 1 ? '' : 's'}`;
  }

  if (days < 365.2425) {
    const months = Math.max(2, Math.floor(days / 30.4375));
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  let years = today.getUTCFullYear() - dob.getUTCFullYear();
  const monthDifference = today.getUTCMonth() - dob.getUTCMonth();
  const dayDifference = today.getUTCDate() - dob.getUTCDate();
  if (
    monthDifference < 0 ||
    (monthDifference === 0 && dayDifference < 0)
  ) {
    years -= 1;
  }

  return `${Math.max(0, years)} year${years === 1 ? '' : 's'}`;
}

function formatCitizenAge(citizen) {
  if (!citizen) return '';

  const fromDob = calculateAgeFromDob(
    citizen.dateOfBirth || citizen.dob || citizen.birthDate
  );
  if (fromDob) {
    const verification =
      citizen.dobStatus === 'verified'
        ? 'DOB verified'
        : citizen.dobStatus === 'reported'
          ? 'DOB reported'
          : '';
    return verification ? `${fromDob} • ${verification}` : fromDob;
  }

  const ageValue = citizen.ageValue;
  const ageUnit = stringValue(citizen.ageUnit);
  if (ageValue !== undefined && ageValue !== null && ageValue !== '') {
    const prefix = citizen.ageIsApproximate ? 'Approximate age: ' : 'Age: ';
    return `${prefix}${ageValue} ${ageUnit || 'years'}`;
  }

  if (citizen.age !== undefined && citizen.age !== null && citizen.age !== '') {
    return `Age: ${citizen.age} years`;
  }

  return 'Age not available';
}

function clearGlobalVisitContext() {
  try {
    const visitId = stringValue(
      localStorage.getItem('activeVisitId') ||
        localStorage.getItem('visitId')
    );

    if (visitId) {
      localStorage.removeItem(`activeVisitContext:${visitId}`);
    }

    localStorage.removeItem('activeVisitId');
    localStorage.removeItem('visitId');
    localStorage.removeItem('activeVisitContext');
    localStorage.removeItem('selectedVisit');
    localStorage.removeItem('editReturnedVisit');
  } catch {
    // Best-effort cleanup.
  }
}

function clearCitizenVisitContext(citizenId) {
  const normalizedCitizenId = stringValue(citizenId);

  try {
    if (normalizedCitizenId) {
      localStorage.removeItem(`activeVisitId:${normalizedCitizenId}`);
    }
    clearGlobalVisitContext();
  } catch {
    // Best-effort cleanup.
  }
}

function persistActiveVisit(citizen, visit) {
  const citizenId = stringValue(citizen?.citizenId);
  const visitId = getVisitId(visit);
  if (!citizenId || !visitId) return '';

  const context = {
    ...visit,
    visitId,
    citizenId,
    centre: normalizeCentreLabel(visit?.centre || citizen?.centre || ''),
  };

  try {
    localStorage.setItem(`activeVisitId:${citizenId}`, visitId);
    localStorage.setItem('activeVisitId', visitId);
    localStorage.setItem('visitId', visitId);
    localStorage.setItem('selectedVisit', JSON.stringify(visit));
    localStorage.setItem('activeVisitContext', JSON.stringify(context));
    localStorage.setItem(`activeVisitContext:${visitId}`, JSON.stringify(context));
  } catch {
    // In-memory selection still remains valid.
  }

  return visitId;
}

function buildRecordPath(sectionPath, citizenId, visitId) {
  const encodedCitizenId = encodeURIComponent(citizenId);
  const encodedVisitId = encodeURIComponent(visitId);

  switch (sectionPath) {
    case 'prescriptions':
      return `/citizen/${encodedCitizenId}/encounter/prescriptions?visitId=${encodedVisitId}`;
    case 'investigations':
      return `/citizen/${encodedCitizenId}/encounter/investigations?visitId=${encodedVisitId}`;
    case 'medical-history':
      return `/citizen/${encodedCitizenId}/encounter/history?visitId=${encodedVisitId}`;
    case 'exam':
      return `/citizen/${encodedCitizenId}/encounter/examination?visitId=${encodedVisitId}`;
    case 'upload-documents':
      return `/citizen/${encodedCitizenId}/upload-documents`;
    case 'upload-slip':
      return `/citizen/${encodedCitizenId}/upload-slip`;
    default:
      return '/';
  }
}

function SortableCard({
  section,
  citizen,
  activeVisit,
  onVisitRequired,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.path });
  const navigate = useNavigate();

  const citizenId = stringValue(citizen?.citizenId);
  const activeVisitId = getVisitId(activeVisit);
  const hasCitizen = Boolean(citizenId);
  const recordReady = !section.requiresVisit || Boolean(activeVisitId);

  const go = () => {
    if (!hasCitizen) return;

    if (section.requiresVisit && !activeVisitId) {
      onVisitRequired?.(section);
      return;
    }

    const target = buildRecordPath(
      section.path,
      citizenId,
      activeVisitId
    );

    navigate(tenantize(target), {
      state: {
        citizenId,
        citizen,
        selectedCitizen: citizen,
        visitId: activeVisitId || undefined,
        visit: activeVisit || undefined,
        visitContext: activeVisit || undefined,
        fromCitizenWorkspace: true,
      },
    });
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 'auto',
    opacity: hasCitizen ? (recordReady ? 1 : 0.68) : 0.45,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        role="button"
        tabIndex={hasCitizen ? 0 : -1}
        aria-disabled={!hasCitizen}
        onClick={go}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            go();
          }
        }}
        className="rounded-2xl shadow-md hover:shadow-xl bg-white dark:bg-[#26272b] border border-gray-200 dark:border-[#363640] transition-colors duration-300 ease-in-out"
        sx={{ cursor: hasCitizen ? 'pointer' : 'not-allowed' }}
        {...attributes}
      >
        <CardContent
          className="flex flex-col items-center justify-center relative"
          style={{
            paddingTop: 32,
            paddingBottom: 32,
            minHeight: 190,
          }}
        >
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="absolute top-2 right-2 cursor-grab active:cursor-grabbing bg-transparent border-0 p-1"
            title="Drag to reorder"
            aria-label={`Reorder ${section.label}`}
            {...listeners}
          >
            <GripVertical
              size={20}
              className="text-[#22733c] dark:text-[#8cffbc] opacity-70"
            />
          </button>

          <div className="mb-3 text-[#22733c] dark:text-[#8cffbc] transition-colors">
            {section.icon}
          </div>

          <Typography
            variant="h6"
            align="center"
            className="text-gray-800 dark:text-gray-100 transition-colors"
          >
            {section.label}
          </Typography>

          {section.requiresVisit && (
            <Typography
              variant="caption"
              align="center"
              sx={{ mt: 1 }}
              color={activeVisitId ? 'success.main' : 'text.secondary'}
            >
              {activeVisitId
                ? `Active Visit: ${activeVisitId}`
                : 'Select a Visit before opening'}
            </Typography>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function GroupCitizen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { citizen, setCitizen } = useCitizen();

  const user = readJson('loggedInUser', {}) || {};
  const role = normalizeRole(user?.role || user?.userType);
  const isCmfmUser = CMFM_SEARCH_ROLES.has(role);

  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitsError, setVisitsError] = useState('');
  const [workspaceMessage, setWorkspaceMessage] = useState('');
  const [activeVisitId, setActiveVisitId] = useState('');
  const [sections, setSections] = useState(() =>
    baseSections.filter((section) =>
      section.roles.map(normalizeRole).includes(role)
    )
  );

  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  useEffect(() => {
    const allowedPaths = new Set(
      baseSections
        .filter((section) =>
          section.roles.map(normalizeRole).includes(role)
        )
        .map((section) => section.path)
    );

    setSections((current) => {
      const currentAllowed = current.filter((section) =>
        allowedPaths.has(section.path)
      );
      const existingPaths = new Set(currentAllowed.map((section) => section.path));
      const missing = baseSections.filter(
        (section) =>
          allowedPaths.has(section.path) &&
          !existingPaths.has(section.path)
      );

      return [...currentAllowed, ...missing];
    });
  }, [role]);

  useEffect(() => {
    const stateCitizen = normalizeCitizen(
      location?.state?.citizen ||
        location?.state?.selectedCitizen ||
        location?.state?.activeCitizen ||
        null
    );
    const citizenIdFromState = stringValue(
      location?.state?.citizenId ||
        stateCitizen?.citizenId
    );

    if (stateCitizen?.citizenId) {
      setCitizen(stateCitizen);
      persistCitizenContext(stateCitizen);
    }

    if (!citizenIdFromState) return undefined;

    const controller = new AbortController();

    http
      .get(
        `/api/citizens/${encodeURIComponent(citizenIdFromState)}`,
        { signal: controller.signal }
      )
      .then((response) => {
        if (controller.signal.aborted) return;

        const loadedCitizen = normalizeCitizen(
          unwrapCitizenResponse(response),
          citizenIdFromState
        );
        if (!loadedCitizen || loadedCitizen.citizenId !== citizenIdFromState) {
          return;
        }

        const mergedCitizen = normalizeCitizen(
          {
            ...(stateCitizen || {}),
            ...loadedCitizen,
            citizenId: citizenIdFromState,
          },
          citizenIdFromState
        );

        if (!mergedCitizen) return;

        setCitizen(mergedCitizen);
        persistCitizenContext(mergedCitizen);
      })
      .catch((error) => {
        if (
          controller.signal.aborted ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return;
        }
        console.warn(
          'GroupCitizen profile refresh failed:',
          getErrorMessage(error, 'Unable to refresh Citizen profile')
        );
      });

    return () => controller.abort();
  }, [
    location.state?.citizenId,
    location.state?.citizen,
    location.state?.selectedCitizen,
    location.state?.activeCitizen,
    setCitizen,
  ]);

  useEffect(() => {
    const normalizedCurrentCitizen = normalizeCitizen(citizen);

    if (normalizedCurrentCitizen?.citizenId) {
      persistCitizenContext(normalizedCurrentCitizen);
      return;
    }

    const cachedCitizen = normalizeCitizen(
      readJson('activeCitizen', null)
    );
    if (cachedCitizen?.citizenId) {
      setCitizen(cachedCitizen);
    }
  }, [citizen, setCitizen]);

  useEffect(() => {
    const citizenId = stringValue(citizen?.citizenId);

    if (!citizenId) {
      setVisits([]);
      setVisitsError('');
      setActiveVisitId('');
      return undefined;
    }

    const scopedVisitId = stringValue(
      localStorage.getItem(`activeVisitId:${citizenId}`)
    );
    setActiveVisitId(scopedVisitId);

    const controller = new AbortController();

    async function loadVisits() {
      setVisitsLoading(true);
      setVisitsError('');

      const encodedCitizenId = encodeURIComponent(citizenId);
      const endpoints = [
        `/api/citizens/${encodedCitizenId}/visits`,
        `/api/visits?citizenId=${encodedCitizenId}`,
      ];

      let loadedVisits = null;
      let lastError = null;

      for (let index = 0; index < endpoints.length; index += 1) {
        try {
          const response = await http.get(
            endpoints[index],
            { signal: controller.signal }
          );
          const payload =
            response && Object.prototype.hasOwnProperty.call(response, 'data')
              ? response.data
              : response;
          loadedVisits = normalizeVisitList(payload);

          // A successful canonical response, including an empty list, is
          // authoritative. The second endpoint is only a compatibility fallback.
          if (index === 0 || loadedVisits.length > 0) break;
        } catch (error) {
          if (
            controller.signal.aborted ||
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            return;
          }

          lastError = error;
          const status = statusOf(error);
          if (index === 0 && ![404, 405].includes(status)) break;
        }
      }

      if (controller.signal.aborted) return;

      if (loadedVisits === null) {
        setVisits([]);
        setVisitsError(
          getErrorMessage(lastError, 'Unable to load previous Visits.')
        );
        setVisitsLoading(false);
        return;
      }

      const sortedVisits = [...loadedVisits].sort(
        (left, right) =>
          getVisitTimestamp(right) - getVisitTimestamp(left)
      );

      setVisits(sortedVisits);

      const restoredVisit = sortedVisits.find(
        (visit) => getVisitId(visit) === scopedVisitId
      );

      if (scopedVisitId && !restoredVisit) {
        clearGlobalVisitContext();
        try {
          localStorage.removeItem(`activeVisitId:${citizenId}`);
        } catch {}
        setActiveVisitId('');
      } else if (restoredVisit) {
        persistActiveVisit(citizen, restoredVisit);
        setActiveVisitId(scopedVisitId);
      }

      setVisitsLoading(false);
    }

    loadVisits();

    return () => controller.abort();
  }, [citizen?.citizenId]); // Citizen ID is the Visit-list identity boundary.

  const activeVisit = useMemo(
    () =>
      visits.find((visit) => getVisitId(visit) === activeVisitId) ||
      null,
    [visits, activeVisitId]
  );

  const latestVisit = useMemo(() => visits[0] || null, [visits]);

  const citizenDisplayName = useMemo(
    () =>
      citizen?.name ||
      citizen?.citizenName ||
      citizen?.fullName ||
      'Selected Citizen',
    [citizen]
  );

  const citizenDisplayCentre = useMemo(
    () =>
      normalizeCentreLabel(
        citizen?.centre ||
          activeVisit?.centre ||
          latestVisit?.centre ||
          ''
      ),
    [citizen?.centre, activeVisit, latestVisit]
  );

  const citizenDisplayAge = useMemo(
    () => formatCitizenAge(citizen),
    [citizen]
  );

  const latestVisitStatusLabel = useMemo(
    () => getVisitStatusLabel(latestVisit),
    [latestVisit]
  );

  const citizenSummaryCards = useMemo(() => {
    if (!citizen?.citizenId) return [];

    return [
      { label: 'Selected citizen', value: citizenDisplayName },
      { label: 'Citizen ID', value: citizen.citizenId },
      { label: 'Centre context', value: citizenDisplayCentre || '—' },
      {
        label: 'Active clinical Visit',
        value: activeVisitId || 'Not selected',
      },
      {
        label: 'Latest workflow status',
        value: latestVisitStatusLabel,
      },
    ];
  }, [
    citizen?.citizenId,
    citizenDisplayName,
    citizenDisplayCentre,
    activeVisitId,
    latestVisitStatusLabel,
  ]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(
      (section) => section.path === active.id
    );
    const newIndex = sections.findIndex(
      (section) => section.path === over.id
    );
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(sections, oldIndex, newIndex);
    setSections(newOrder);

    http
      .post('/api/dashboard-order', {
        group: 'citizen',
        role,
        order: newOrder.map((section) => section.path),
      })
      .catch(() => {
        // Existing local ordering remains usable if persistence is unavailable.
      });
  };

  function selectVisit(visit) {
    const visitId = persistActiveVisit(citizen, visit);
    if (!visitId) return;

    setActiveVisitId(visitId);
    setWorkspaceMessage(`Visit ${visitId} selected for clinical records.`);
  }

  function clearSelectionInPlace() {
    const citizenId = stringValue(citizen?.citizenId);

    clearCitizenVisitContext(citizenId);

    try {
      localStorage.removeItem('activeCitizen');
      localStorage.removeItem('activeCitizenId');
      localStorage.removeItem('selectedCitizen');
      localStorage.removeItem('lastVisitedCitizenId');
      localStorage.removeItem('activeCentre');
      localStorage.removeItem('centre');
    } catch {
      // Best-effort cleanup.
    }

    setVisits([]);
    setVisitsError('');
    setActiveVisitId('');
    setWorkspaceMessage('');
    setCitizen(null);
  }

  function handleVisitRequired(section) {
    setWorkspaceMessage(
      `Select a Visit below before opening "${section.label}". Start a new Visit when no suitable Visit exists.`
    );
  }

  const goPrev = () => {
    const citizenId = stringValue(citizen?.citizenId);
    if (!citizenId) return;

    persistCitizenContext(citizen);
    navigate(
      tenantize(
        `/citizen/${encodeURIComponent(citizenId)}/previous-visits`
      ),
      {
        state: {
          citizenId,
          citizen,
          selectedCitizen: citizen,
          visitId: activeVisitId || undefined,
          visit: activeVisit || undefined,
        },
      }
    );
  };

  const goAdd = () => {
    const citizenId = stringValue(citizen?.citizenId);
    if (!citizenId) return;

    persistCitizenContext(citizen);
    clearGlobalVisitContext();
    setActiveVisitId('');
    setWorkspaceMessage('');

    navigate(
      tenantize(`/citizen/${encodeURIComponent(citizenId)}/add-visit`),
      {
        state: {
          citizenId,
          citizen,
          selectedCitizen: citizen,
          fromCitizenWorkspace: true,
        },
      }
    );
  };

  const goOperations = () => {
    navigate(tenantize('/dashboard/operations'), {
      state: citizen?.citizenId
        ? {
            citizen,
            selectedCitizen: citizen,
            citizenId: citizen.citizenId,
            visitId: activeVisitId || undefined,
            visit: activeVisit || undefined,
          }
        : undefined,
    });
  };

  return (
    <Box className="bg-white dark:bg-[#121212] text-black dark:text-white transition-colors duration-300 min-h-screen w-full p-4">
      <div className="flex flex-col gap-2">
        {isCmfmUser && (
          <CitizenSearchBar
            onResult={(selectedCitizen) => {
              const normalized = normalizeCitizen(selectedCitizen);
              if (!normalized?.citizenId) return;

              setCitizen(normalized);
              persistCitizenContext(normalized);
              setVisits([]);
              setVisitsError('');
              setActiveVisitId('');
              setWorkspaceMessage('');
            }}
          />
        )}

        {!citizen?.citizenId && (
          <div className="mb-4 rounded-xl border border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-[#102417] px-4 py-3 text-sm text-emerald-900 dark:text-emerald-200">
            Use this page as a citizen-specific workspace. For new Citizen
            registration and the main operational launch flow, use Comprehensive
            Services.
            {isCmfmUser && (
              <button
                type="button"
                onClick={goOperations}
                className="ml-3 rounded-lg bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 text-white"
              >
                Open Comprehensive Services
              </button>
            )}
          </div>
        )}

        {workspaceMessage && citizen?.citizenId && (
          <Alert
            severity={activeVisitId ? 'success' : 'warning'}
            onClose={() => setWorkspaceMessage('')}
            sx={{ mb: 2 }}
          >
            {workspaceMessage}
          </Alert>
        )}

        {visitsError && citizen?.citizenId && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {visitsError}
          </Alert>
        )}

        {citizen?.citizenId && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
              {citizenSummaryCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-green-300 dark:border-green-800 bg-[#f6fff8] dark:bg-[#102417] p-4 shadow-sm"
                >
                  <Typography
                    variant="caption"
                    className="text-gray-600 dark:text-gray-400 block mb-1"
                  >
                    {card.label}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    className="font-semibold text-green-900 dark:text-green-200 break-words"
                  >
                    {card.value}
                  </Typography>
                </div>
              ))}
            </div>

            <div className="mb-4 p-4 rounded-xl bg-[#e3fbe8] dark:bg-[#143e21] border border-green-300 dark:border-green-800 shadow flex flex-col gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-green-900 dark:text-green-200">
                  Citizen ID:{' '}
                  <span className="font-mono">{citizen.citizenId}</span>
                </span>

                {activeVisit ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={`Active Visit: ${getVisitId(activeVisit)}`}
                  />
                ) : latestVisit ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Latest Visit: ${getVisitId(latestVisit)}`}
                  />
                ) : null}

                <button
                  type="button"
                  onClick={clearSelectionInPlace}
                  className="ml-auto bg-gray-200 hover:bg-gray-300 dark:bg-[#0f2416] dark:hover:bg-[#14301d] text-sm rounded-lg px-3 py-1"
                >
                  Clear selection
                </button>
              </div>

              <span className="text-lg text-[#235d2b] dark:text-[#8cffbc]">
                {citizenDisplayName}
                {citizenDisplayAge ? ` | ${citizenDisplayAge}` : ''}
                {citizen.gender ? ` | Gender: ${citizen.gender}` : ''}
                {citizenDisplayCentre
                  ? ` | Centre: ${citizenDisplayCentre}`
                  : ''}
              </span>

              <div className="text-sm text-gray-700 dark:text-gray-300">
                <div className="mb-1">
                  Previous Visits and saved Citizen records:
                </div>

                <div style={{ minHeight: 88 }}>
                  {visitsLoading && visits.length === 0 ? (
                    <>
                      <Skeleton variant="text" width={300} height={22} />
                      <Skeleton variant="text" width={260} height={22} />
                      <Skeleton variant="text" width={220} height={22} />
                    </>
                  ) : visits.length > 0 ? (
                    <ul className="space-y-2">
                      {visits.slice(0, 3).map((visit, index) => {
                        const visitId = getVisitId(visit);
                        const selected = visitId === activeVisitId;

                        return (
                          <li
                            key={visitId || index}
                            className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-green-200 dark:border-green-900 bg-white/70 dark:bg-black/10 px-3 py-2"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium break-words">
                                {visitId || 'Visit ID unavailable'}
                              </div>
                              <div className="text-xs opacity-80">
                                {formatVisitDate(visit)}
                                {' • '}
                                {getVisitStatusLabel(visit)}
                                {visit.centre
                                  ? ` • ${normalizeCentreLabel(visit.centre)}`
                                  : ''}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => selectVisit(visit)}
                              className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${
                                selected
                                  ? 'bg-green-700 text-white'
                                  : 'bg-white dark:bg-[#102417] border border-green-500 text-green-800 dark:text-green-200'
                              }`}
                            >
                              {selected ? 'Selected' : 'Use this Visit'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <span className="opacity-70">No Visits yet.</span>
                  )}
                </div>

                {visitsLoading && visits.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 opacity-70">
                    <CircularProgress size={14} />
                    <span>Refreshing…</span>
                  </div>
                )}

                {visits.length > 3 && (
                  <div className="mt-2 text-xs opacity-75">
                    Showing the latest 3 of {visits.length} Visits. Open Previous
                    Visits to review the complete list.
                  </div>
                )}
              </div>

              <div className="mt-3 flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={goPrev}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-xl font-semibold shadow"
                >
                  View Previous Visits
                </button>

                <button
                  type="button"
                  onClick={goAdd}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl font-semibold shadow"
                >
                  Add New Visit
                </button>

                {isCmfmUser && (
                  <button
                    type="button"
                    onClick={goOperations}
                    className="bg-slate-700 hover:bg-slate-800 text-white px-4 py-2 rounded-xl font-semibold shadow"
                  >
                    Open Comprehensive Services
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Typography
        variant="h6"
        className="font-semibold mb-1 text-[#22733c] dark:text-[#8cffbc] transition-colors"
      >
        Citizen Workspace
      </Typography>

      <Typography
        variant="body2"
        className="mb-2 text-gray-600 dark:text-gray-300 transition-colors"
      >
        Use this area for Citizen-specific records, review and follow-up work.
      </Typography>

      <Typography
        variant="body2"
        className="mb-4 text-gray-600 dark:text-gray-300 transition-colors"
      >
        Clinical record cards use the explicitly selected active Visit.
        Comprehensive Services remains the main launch surface for new
        registration, Visit initiation and wider operational flow.
      </Typography>

      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sections.map((section) => section.path)}
          strategy={rectSortingStrategy}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {sections.map((section) => (
              <SortableCard
                key={section.path}
                section={section}
                citizen={citizen}
                activeVisit={activeVisit}
                onVisitRequired={handleVisitRequired}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
